/**
 * Keeps the free-shipping progress bar in sync with the cart after
 * add/update/remove, without a page reload.
 *
 * Hooks into Dawn's OWN pub/sub system (assets/pubsub.js), the same one
 * assets/cart.js and assets/product-form.js already publish to on every cart
 * mutation. `PUB_SUB_EVENTS` and `subscribe` are globals by the time this
 * runs: constants.js and pubsub.js are loaded as plain deferred <script> tags
 * in theme.liquid's <head>, ahead of this file, and `defer` scripts execute
 * in source order per the HTML spec.
 *
 * On every cartUpdate event this does its OWN fresh GET to /cart.js rather
 * than trusting the event payload's shape. Confirmed live this matters:
 * cart.js's quantity-update path publishes the full cart (cartData.total_price
 * present), but product-form.js's add-to-cart path publishes the /cart/add.js
 * response instead -- the newly added LINE ITEM, which has no total_price at
 * all. Trusting that shape left the bar showing stale pre-add numbers after
 * every fresh "Add to cart" click.
 *
 * Wrapped in an IIFE because this snippet is rendered in more than one
 * always-present template fragment on the same page (cart-notification and
 * cart-drawer both exist in the DOM regardless of the merchant's cart_type
 * setting) -- confirmed live: without this wrapper, the second copy of this
 * <script> tag throws "Identifier 'FreeShippingBar' has already been
 * declared" because a top-level `class` is a redeclaration across separate
 * classic <script> tags in the same document, unlike `var`.
 */
(function () {
  if (customElements.get('free-shipping-bar')) return;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this.thresholdCents = parseInt(this.dataset.thresholdCents, 10) || 5000;

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
    }

    refresh() {
      fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
        .then((r) => r.json())
        .then((cart) => {
          if (typeof cart.total_price === 'number') this.render(cart.total_price);
        })
        .catch(() => {
          /* Network hiccup: leave the last-known state on screen rather than
             show something misleading. It'll correct on the next update. */
        });
    }

    render(currentCents) {
      const messageEl = this.querySelector('[data-free-shipping-message]');
      const fillEl = this.querySelector('[data-free-shipping-fill]');
      if (!messageEl || !fillEl) return;

      const percent = Math.min(100, (currentCents / this.thresholdCents) * 100);
      fillEl.style.width = `${percent}%`;
      this.querySelector('.free-shipping-bar__track')?.setAttribute('aria-valuenow', String(Math.round(percent)));

      if (currentCents >= this.thresholdCents) {
        messageEl.textContent = "You've unlocked free shipping.";
      } else {
        const remaining = (this.thresholdCents - currentCents) / 100;
        const formatted = remaining.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        messageEl.textContent = `Add ${formatted} more for free shipping.`;
      }
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
})();
